import { Route, Routes, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AccessDenied, useUsers } from "common";
import StudyListingPage from "./components/study/StudyListingPage";
import ImportProspectsPage from "./components/prospect/ImportProspectsPage";
import ConductOutreachPage from "./components/outreach/ConductOutreachPage";
import CreateEditStudyForm from "./components/study/edit/CreateEditStudyForm";
import { ProspectProvider } from "./services/prospectContext";
import ProspectListingPage from "./components/prospect/ProspectListingPage";
import PortfolioReports from "./components/reports/PortfolioReports";
import EditProspectPage from "./components/prospect/EditProspectPage";
import StudyReports from "./components/reports/StudyReports";
import MyStudiesPage from "./components/study/mystudies/MyStudiesPage";
import MyStudyProvider from "./services/myStudyContext";
import NoStudyAssociatedErrorPage from "./components/study/errors/NoStudyAssociatedErrorPage";
import { StudyListingPageContent } from "./components/study/website/StudyListingPageContent";
import StudyInsightsPageContent from "app/components/study/insights/StudyInsightsPageContent";
import StudyRecruitmentPage from "app/components/StudyRecruitment/StudyRecruitmentPage";
import { StudyRecruitmentProvider } from "./services/StudyRecruitmentService/StudyRecruitmentProvider";
import ActivityLogPage from "./components/activitylog/ActivityLogPage";

export const AppRoutes = () => {
  const { accessDenied, currentUser } = useUsers();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // Prevent access if the user is not authorized to use the application
  if (accessDenied) {
    return <AccessDenied supportEmail={t(`app.supportEmail`)} />;
  }

  const isStudyTeamUser =
    Array.isArray(currentUser.roles) &&
    currentUser.roles.length > 0 &&
    currentUser.roles.every((r) => r.role === "PI" || r.role === "CRC");

  const isSystemAdmin =
    Array.isArray(currentUser.roles) &&
    currentUser.roles.some((r) => r.role === "SYSTEM_ADMIN");

  if (currentUser.isLoaded && isStudyTeamUser && location.pathname === "/") {
    navigate("/study-recruitment");
  }

  return (
    <Routes>
      <Route path="/" element={<StudyListingPage />} />
      <Route
        path="/prospects/:studyId/import"
        element={<ImportProspectsPage />}
      />
      <Route
        path="/outreach/:studyId/conduct"
        element={<ConductOutreachPage />}
      />

      <Route path="/studies/:studyId/edit" element={<CreateEditStudyForm />} />
      <Route
        path="/studies/myStudies"
        element={
          <MyStudyProvider>
            <MyStudiesPage />
          </MyStudyProvider>
        }
      />
      <Route
        path="/studies/noStudyAssociationFound"
        element={<NoStudyAssociatedErrorPage />}
      />
      <Route
        path="/studies/:protocolNumber/listingPage"
        element={<StudyListingPageContent />}
      />
      <Route
        path="/studies/:protocolNumber/insights"
        element={<StudyInsightsPageContent />}
      />
      <Route
        path="/prospects/lookup"
        element={
          <ProspectProvider>
            <ProspectListingPage />
          </ProspectProvider>
        }
      />
      <Route
        path="/prospects/:prospectId/edit"
        element={
          <ProspectProvider>
            <EditProspectPage />
          </ProspectProvider>
        }
      />
      <Route
        path="/portfolio/reports"
        element={
          <ProspectProvider>
            <PortfolioReports />
          </ProspectProvider>
        }
      />
      <Route
        path="/study/reports"
        element={
          <ProspectProvider>
            <StudyReports />
          </ProspectProvider>
        }
      />
      <Route
        path="/study-recruitment"
        element={
          <StudyRecruitmentProvider>
            <StudyRecruitmentPage />
          </StudyRecruitmentProvider>
        }
      />
      <Route
        path="/study-recruitment/:protocolNumber"
        element={
          <StudyRecruitmentProvider>
            <StudyRecruitmentPage />
          </StudyRecruitmentProvider>
        }
      />
      <Route
        path="/study-recruitment/noStudyAssociationFound"
        element={<NoStudyAssociatedErrorPage />}
      />
      <Route
        path="/study-recruitment/:protocolNumber/noStudyAssociationFound"
        element={<NoStudyAssociatedErrorPage singleStudy />}
      />
      <Route
        path="/activity-log"
        element={
          isSystemAdmin ? (
            <ActivityLogPage />
          ) : (
            <AccessDenied supportEmail={t(`app.supportEmail`)} />
          )
        }
      />
    </Routes>
  );
};
